import React from "react";

import { toTieredEntries } from "./iarFeeTieredUtils";
import {
  getFusionAllocationDisplayName,
  getProgramDisplayName,
} from "../../utils/proposalUtils";

// Compact label/value row — replaces the prior tile grid. Grouped under
// section headings below so the confirm view is scannable on a single laptop
// screen for typical (single-client, non-Fusion) submissions.
const Row = ({ label, value }) => (
  <div
    className="d-flex py-1"
    style={{ borderBottom: "1px solid #f0f0f0", fontSize: "14px" }}
  >
    <div
      style={{
        flex: "0 0 240px",
        color: "#6c757d",
        paddingRight: "12px",
      }}
    >
      {label}
    </div>
    <div style={{ flex: 1, fontWeight: 500, wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);

const SectionHeading = ({ children }) => (
  <h5
    style={{
      color: "#385EA4",
      borderBottom: "2px solid #385EA4",
      paddingBottom: "4px",
      marginTop: "20px",
      marginBottom: "8px",
      fontSize: "15px",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}
  >
    {children}
  </h5>
);

const FeeStageHeading = ({ children }) => (
  <div
    style={{
      color: "#385EA4",
      fontWeight: 700,
      fontSize: "13px",
      textTransform: "uppercase",
      marginTop: "12px",
      paddingTop: "8px",
      borderTop: "1px solid #dee2e6",
    }}
  >
    {children}
  </div>
);

const ISPConfirm = ({
  state,
  lists,
  isFormValid,
  onSubmit,
  isSubmitting,
  submitError,
  viewOnly = false,
}) => {
  const {
    advisorName,
    custodian,
    strategy,
    strategyDisplayName,
    bondType,
    pathStrategy,
    pathBondType,
    pathIarFeeType,
    pathIarFeeFlat,
    pathIarFeeTiered,
    hasPath,
    registrations,
    requestType,
    existingRequestAction,
    accountNumber,
    accountNumbers,
    clientProfileImaSubmitted,
    householding,
    householdRefName,
    householdRefLast4,
    householdRef,
    householdReason,
    householdingMode,
    initialInvestmentAmount,
    accountNetWorthPct,
    accountIncomeNeed,
    accountGoal,
    accountGoalOther,
    sourceOfFunds,
    minorName,
    relationshipToCustodian,
    businessName,
    businessEinLast4,
    fusionRegistrationType,
    fusionRebalanceType,
    jointClients,
    combinedScore,
    riskCategory,
    selectedPortfolio,
    portfolioOverride,
    iarFeeTier,
    iarFeeType,
    iarFeeFlat,
    iarFeeTiered,
    iarFeeScheduleType,
    managementFees,
    fusionAllocations,
    fusionSubFees,
    selectedManagementFeeTierKey,
    fusionAllocationRiskValidation,
  } = state;

  const isFusion = strategy?.toLowerCase().includes("fusion") || false;
  const isFusionMultiRegistration = isFusion && fusionRegistrationType === "Multi";
  const isModelChange = existingRequestAction === "Model Change";
  const showClientProfileIma =
    requestType === "New" ||
    (requestType === "Existing" && existingRequestAction === "Strategy Change");
  const showHouseholding =
    requestType === "New" && householdingMode !== "disallowed";
  const effectiveFusionRebalanceType = isFusionMultiRegistration
    ? "Manual"
    : fusionRebalanceType || "Automatic";
  const strategyDisplay = strategyDisplayName ||
    getProgramDisplayName(strategy, lists?.proposalTypes);

  const primaryClient = jointClients.find((c) => c.isPrimary);
  const registrationValue = isFusionMultiRegistration
    ? ""
    : registrations[0]?.value || "";
  const registrationDisplay = isFusionMultiRegistration
    ? ""
    : (registrations || [])
        .map((registration) => registration.value)
        .filter(Boolean)
        .join(", ");
  const accountNumberDisplay = (
    accountNumbers && accountNumbers.length
      ? accountNumbers
      : accountNumber
        ? [accountNumber]
        : []
  )
    .map((number) => String(number || "").trim())
    .filter(Boolean)
    .map((number) => `****${number}`)
    .join(", ");
  const requestTypeDescription = requestType
    ? requestType === "New"
      ? "New Business"
      : "Existing Account Update"
    : "\u2014";
  const riskScoreDescription =
    combinedScore !== "" || riskCategory
      ? `${combinedScore !== "" ? combinedScore : "\u2014"}/100${
          riskCategory ? ` \u2014 ${riskCategory}` : ""
        }`
      : "\u2014";
  const portfolioDescription = selectedPortfolio
    ? `${selectedPortfolio}${portfolioOverride ? " \u2014 Override" : ""}`
    : "\u2014";
  const fusionAllocationRiskMessage =
    isFusion &&
    fusionAllocationRiskValidation &&
    fusionAllocationRiskValidation.isValid === false
      ? fusionAllocationRiskValidation.message
      : "";

  // T8 \u2014 household reference now carries name + last-4 separately. Fall
  // back to the legacy householdRef field for older drafts that pre-date
  // the split.
  const householdingValue =
    householding === "Yes"
      ? `Yes \u2014 ${householdRefName ? `${householdRefName}, ` : ""}Account ****${
          householdRefLast4 || householdRef || ""
        }`
      : householding === "No"
      ? `Not Applicable${householdReason ? ` \u2014 ${householdReason}` : ""}`
      : "\u2014";

  const goalValue = Array.isArray(accountGoal)
    ? accountGoal.length
      ? accountGoal
          .map((goal) =>
            goal === "Other" && accountGoalOther
              ? `Other: ${accountGoalOther}`
              : goal,
          )
          .join(", ")
      : "\u2014"
    : accountGoal === "Funding for another goal"
    ? accountGoalOther || "Funding for another goal"
    : accountGoal || "\u2014";

  const formatPct = (v) =>
    v === "" || v === null || v === undefined || isNaN(Number(v))
      ? "\u2014"
      : `${Number(v).toFixed(2)}%`;

  const StackedSchedule = ({ entries }) => (
    <div style={{ display: "grid", gap: "2px", maxWidth: "520px" }}>
      {entries.map(({ label, rate }, index) => (
        <div
          key={`${label}-${index}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <span>{label}</span>
          <span style={{ whiteSpace: "nowrap" }}>{formatPct(rate)}</span>
        </div>
      ))}
    </div>
  );

  const renderTierSchedule = (entries) =>
    entries.length ? <StackedSchedule entries={entries} /> : "\u2014";

  const forceFixedIarFee =
    hasPath || (managementFees?.flatFee !== undefined && managementFees?.flatFee !== null);
  const initialIarFeeType = iarFeeType || (forceFixedIarFee ? "Fixed Percentage" : "");
  const iarFeeValue =
    initialIarFeeType === "Fixed Percentage"
      ? `Fixed Percentage \u2014 ${formatPct(iarFeeFlat)}`
      : initialIarFeeType === "Tiered"
      ? `Tiered (${iarFeeScheduleType})`
      : "\u2014";

  // T17 \u2014 surface the IM fee schedule on the Confirm screen so reviewers
  // see both fee components, not only the IAR fee.
  // T4 \u2014 for Fusion, build per-sleeve summary lines from fusionSubFees;
  // for non-Fusion, fall back to the single managementFees schedule.
  const fusionFeeSummaries =
    isFusion && Array.isArray(fusionAllocations)
      ? fusionAllocations
          .filter((a) => a && a.strategy)
          .map((a) => {
            const tier = selectedManagementFeeTierKey || "";
            const key = `${a.strategy}::${tier}`;
            const fees = fusionSubFees?.[key] || fusionSubFees?.[a.strategy];
            const rates = fees?.tieredRates || [];
            const entries = rates.map((t) => ({ label: t.label, rate: t.rate }));
            const allocationLabel =
              a.displayName ||
              getFusionAllocationDisplayName(a, lists?.proposalTypes || []);
            const allocationDetails = [
              a.accountOwner && `Owner: ${a.accountOwner}`,
              a.registration && `Registration: ${a.registration}`,
            ].filter(Boolean);
            return {
              strategy: allocationDetails.length
                ? `${allocationLabel} (${allocationDetails.join("; ")})`
                : allocationLabel,
              weight: a.percentage,
              entries,
            };
          })
      : [];

  const imTiered =
    managementFees?.tieredRates && Array.isArray(managementFees.tieredRates)
      ? managementFees.tieredRates
      : [];
  const imTieredEntries = imTiered.map((t) => ({
    label: t.label,
    rate: t.rate,
  }));

  const iarTieredEntries =
    initialIarFeeType === "Tiered" ? toTieredEntries(iarFeeTiered) : [];

  const pathIarTieredEntries =
    hasPath && pathIarFeeType === "Tiered" ? toTieredEntries(pathIarFeeTiered) : [];

  return (
    <div>
      {!viewOnly && <h1>Confirm:</h1>}

      <div style={{ margin: "12px 0 20px" }}>
        <SectionHeading>Client Information</SectionHeading>
        <Row
          label="Investment Advisor Representative (IAR)"
          value={advisorName || "\u2014"}
        />
        <Row label="Request Type" value={requestTypeDescription} />
        {existingRequestAction && (
          <Row label="Account Update Type" value={existingRequestAction} />
        )}
        {accountNumberDisplay && (
          <Row
            label="Account Number(s) (Last 4 digits)"
            value={accountNumberDisplay}
          />
        )}
        {showClientProfileIma && (
          <Row
            label="Client Profile + IMA submitted"
            value={clientProfileImaSubmitted || "\u2014"}
          />
        )}
        {showHouseholding && <Row label="Householding" value={householdingValue} />}
        {primaryClient && (
          <Row
            label="Primary Account Holder"
            value={
              primaryClient.ssnLast4
                ? `${primaryClient.clientName}  (SSN ****${primaryClient.ssnLast4})`
                : primaryClient.clientName
            }
          />
        )}
        {jointClients.length > 1 && (
          <Row
            label="Account Holders"
            value={jointClients.map((c) => c.clientName).join(", ")}
          />
        )}

        <SectionHeading>Account Information</SectionHeading>
        {custodian && <Row label="Custodian" value={custodian} />}
        {isFusion && (
          <Row
            label="Registration Type"
            value={fusionRegistrationType || "Single"}
          />
        )}
        {isFusion && (
          <Row
            label="Rebalance Type"
            value={effectiveFusionRebalanceType}
          />
        )}
        {!isFusionMultiRegistration && (
          <Row
            label="Registration"
            value={registrationDisplay || registrationValue || "\u2014"}
          />
        )}
        {!isFusionMultiRegistration && (minorName || relationshipToCustodian) && (
          <Row
            label="UGMA/UTMA Minor"
            value={
              [minorName, relationshipToCustodian && `(${relationshipToCustodian})`]
                .filter(Boolean)
                .join(" ")
            }
          />
        )}
        {!isFusionMultiRegistration && (businessName || businessEinLast4) && (
          <Row
            label="Business Registration"
            value={
              [
                businessName,
                businessEinLast4 && `(EIN ****${businessEinLast4})`,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        )}
        <Row label="Strategy" value={strategyDisplay || "\u2014"} />
        <Row label="Bond Type" value={bondType || "Taxable"} />
        {pathBondType && (
          <Row
            label="Path Bond Type"
            value={
              pathStrategy
                ? `${pathBondType} (${pathStrategy})`
                : pathBondType
            }
          />
        )}
        <Row
          label="Initial Investment Amount"
          value={initialInvestmentAmount || "\u2014"}
        />
        {accountNetWorthPct && (
          <Row label="% of Net Worth" value={`${accountNetWorthPct}%`} />
        )}
        <Row label="Income Need" value={accountIncomeNeed || "No"} />
        <Row label="Goal" value={goalValue} />
        <Row
          label="Source of Funds"
          value={sourceOfFunds.join(", ") || "\u2014"}
        />

        {existingRequestAction !== "Fee Update" && (
          <>
            <SectionHeading>Risk Tolerance</SectionHeading>
            {!isModelChange && (
              <Row label="Risk Score Total" value={riskScoreDescription} />
            )}
            <Row
              label={isModelChange ? "Allocation" : "Portfolio"}
              value={portfolioDescription}
            />
          </>
        )}

        <SectionHeading>Fees</SectionHeading>
        {hasPath ? (
          <>
            <FeeStageHeading>Initial Stage Fees</FeeStageHeading>
            <Row label="IAR Fee" value={iarFeeValue} />
            {iarTieredEntries.length > 0 && (
              <Row
                label="IAR Fee Tier Schedule"
                value={renderTierSchedule(iarTieredEntries)}
              />
            )}
            {managementFees?.flatFee != null && (
              <Row
                label="Investment Management Fee"
                value={formatPct(managementFees.flatFee)}
              />
            )}
            {managementFees?.minimumFee && (
              <Row
                label="Investment Management Minimum Account Fee"
                value={`$${managementFees.minimumFee}`}
              />
            )}

            <FeeStageHeading>Path Strategy Fees</FeeStageHeading>
            <Row
              label="IAR Fee"
              value={
                pathIarFeeType === "Fixed Percentage"
                  ? `Fixed Percentage \u2014 ${formatPct(pathIarFeeFlat)}`
                  : pathIarFeeType === "Tiered"
                  ? "Tiered"
                  : "\u2014"
              }
            />
            {pathIarTieredEntries.length > 0 && (
              <Row
                label="IAR Fee Tier Schedule"
                value={renderTierSchedule(pathIarTieredEntries)}
              />
            )}
            {iarFeeTier && iarFeeTier !== "Standard" && (
              <Row label="Investment Management Fee Tier" value={iarFeeTier} />
            )}
            <Row
              label="Investment Management Fee"
              value={renderTierSchedule(imTieredEntries)}
            />
            {managementFees?.pathMinimumFee && (
              <Row
                label="Investment Management Minimum Account Fee"
                value={`$${managementFees.pathMinimumFee}`}
              />
            )}
          </>
        ) : (
          <>
            <Row label="IAR Fee" value={iarFeeValue} />
            {iarTieredEntries.length > 0 && (
              <Row
                label="IAR Tier Schedule"
                value={renderTierSchedule(iarTieredEntries)}
              />
            )}
            {iarFeeTier && iarFeeTier !== "Standard" && (
              <Row label="Investment Management Fee Tier" value={iarFeeTier} />
            )}
          </>
        )}
        {/* T17 + T4 - for Fusion, render one row per sub-strategy with its own
            tier schedule; for non-Fusion, render the single IM schedule. */}
        {!hasPath && isFusion ? (
          fusionFeeSummaries.length > 0 ? (
            fusionFeeSummaries.map((s, idx) => (
              <Row
                key={idx}
                label={`IM Schedule - ${s.strategy} (${Number(s.weight).toFixed(0)}%)`}
                value={renderTierSchedule(s.entries)}
              />
            ))
          ) : (
            <Row label="Investment Management Schedule" value="\u2014" />
          )
        ) : !hasPath ? (
          <>
            <Row
              label="Investment Management Schedule"
              value={renderTierSchedule(imTieredEntries)}
            />
            {managementFees?.minimumFee && (
              <Row
                label="IM Minimum Account Fee"
                value={`$${managementFees.minimumFee}`}
              />
            )}
            {managementFees?.flatFee != null && (
              <Row
                label="Fixed Percentage - All Assets"
                value={`All assets: ${formatPct(managementFees.flatFee)}`}
              />
            )}
          </>
        ) : null}
      </div>

      {fusionAllocationRiskMessage && !viewOnly && (
        <div className="alert alert-danger" role="alert">
          {fusionAllocationRiskMessage}
        </div>
      )}

      {/* Error Alert */}
      {submitError && !viewOnly && (
        <div className="alert alert-danger" role="alert">
          {submitError}
        </div>
      )}

      {/* Submit */}
      {!viewOnly && (
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-lg btn-success"
            style={{
              minWidth: "200px",
            }}
            disabled={!isFormValid || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? (
              <span>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                Submitting...
              </span>
            ) : (
              "Submit ISP"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ISPConfirm;
